import { mergeClasses, TreeItem, TreeItemLayout } from '@fluentui/react-components';
import { FC } from 'react';
import { useAccountAmountStyles } from '../../../hooks/useAccountAmountStyles';
import { useDrillDownStyles } from '../../../hooks/useDrillDownStyles';
import { formatNumberForDisplay, parseNumericValue } from '../../../utils/NumberUtils';
import { DefaultTooltipHostV9 } from '../../common/DefaultTooltipHostV9';
import { EnterAmountsDialogButton } from '../EnterAmountsDialogButton';
import { JournalEntryDialogButton } from '../JournalEntryDialogButton';
import { AccountAmountWhitePaperRow } from '../ReportDrillDownUtils';
import { AccountAmountAdjustmentEntryRow } from './AccountAmountAdjustmentEntryRow';
import { EditableNumberDiv } from './EditableNumberDiv';
import { LastModifiedByIcon } from './LastModifiedByIcon';
import { useTreeLineStyles } from './TreeLineUtils';
import { IAccountAmountAdjustmentInfoTreeProps } from './types';

function reformatNumericText(value: string, numberFormatter: (v: number) => string): string {
    return numberFormatter(parseNumericValue(value, true));
}

export const AccountAmountAdjustmentInfoTree: FC<IAccountAmountAdjustmentInfoTreeProps> = (
    props: IAccountAmountAdjustmentInfoTreeProps
) => {
    const styles = useAccountAmountStyles();
    const drillDownStyles = useDrillDownStyles();
    const treeLineStyles = useTreeLineStyles();
    const row: AccountAmountWhitePaperRow = props.row;

    const parentAdjustmentSummaries: AccountAmountWhitePaperRow[] | null = row.parent?.adjustmentSummaries ?? null;
    const lastParentAdjustmentSummary: AccountAmountWhitePaperRow | null =
        parentAdjustmentSummaries && parentAdjustmentSummaries.length > 0
            ? parentAdjustmentSummaries[parentAdjustmentSummaries.length - 1]
            : null;
    const isLastAdjustmentSummary: boolean = !lastParentAdjustmentSummary || lastParentAdjustmentSummary.uiKey === row.uiKey;

    const shouldShowJournalEntry: boolean = props.journalEntryButtonDisplayState !== 'invisible' && !row.adjustmentEntries?.length;
    const shouldShowLastModified: boolean = !!row.lastModifiedBy && !row.adjustmentEntries?.length;
    const shouldShowEnterAmounts: boolean = !row.adjustmentEntries?.length && row.isComplexAdjustment;
    return (
        <>
            <TreeItem itemType='leaf' key={row.uiKey} value={row.uiKey!}>
                <TreeItemLayout
                    main={{ className: mergeClasses(styles.flexFullWidth, styles.treeItemLayoutMain, props.containerClassName) }}
                    className={mergeClasses('adjustment-summary-row', styles.alignItemsStart, styles.summaryRow, props.className)}
                >
                    <div
                        role='gridcell'
                        className={mergeClasses(
                            styles.alignContentCenter,
                            styles.fontWeightSemibold,
                            styles.accountColumn,
                            styles.fontSize
                        )}
                    >
                        {row.adjustmentCode}
                    </div>
                    <div
                        role='gridcell'
                        className={mergeClasses(
                            styles.alignContentCenter,
                            styles.fontWeightSemibold,
                            styles.adjustmentTypeColumn,
                            styles.fontSize
                        )}
                    >
                        {row.adjustmentType}
                    </div>
                    <div
                        role='gridcell'
                        className={mergeClasses(
                            shouldShowLastModified ? styles.descriptionColumnSmall : styles.descriptionColumn,
                            styles.alignContentCenter,
                            styles.fontWeightSemibold,
                            styles.fontSize
                        )}
                        data-automation-key='description'
                    >
                        <DefaultTooltipHostV9 appearance='inverted' withArrow positioning='before' content={row.description ?? ''}>
                            {row.description}
                        </DefaultTooltipHostV9>
                    </div>
                    {shouldShowLastModified || shouldShowJournalEntry || shouldShowEnterAmounts ? (
                        <>
                            <div
                                role='gridcell'
                                className={mergeClasses(styles.alignContentCenter, styles.lastModifiedByColumn, styles.fontSize)}
                            >
                                {shouldShowLastModified ? <LastModifiedByIcon lastModifiedBy={row.lastModifiedBy!} /> : null}
                            </div>
                            <div
                                role='gridcell'
                                className={mergeClasses(
                                    styles.alignContentCenter,
                                    shouldShowEnterAmounts ? styles.disabled : undefined,
                                    styles.journalEntryButtonHeader,
                                    styles.fontSize,
                                    props.rowChangeSummary?.changed ? styles.changedBackground : undefined
                                )}
                            >
                                {shouldShowJournalEntry ? (
                                    <JournalEntryDialogButton
                                        className={drillDownStyles.iconOnlyButton}
                                        disabled={props.journalEntryButtonDisplayState === 'disabled'}
                                        onButtonClick={props.onJournalEntryClick!}
                                    />
                                ) : null}
                            </div>
                            <div
                                role='gridcell'
                                className={mergeClasses(
                                    styles.alignContentCenter,
                                    shouldShowEnterAmounts ? styles.disabled : undefined,
                                    styles.enterAmountsButtonHeader,
                                    styles.fontSize,
                                    props.rowChangeSummary?.changed ? styles.changedBackground : undefined
                                )}
                            >
                                {shouldShowEnterAmounts ? (
                                    <EnterAmountsDialogButton
                                        className={drillDownStyles.iconOnlyButton}
                                        onButtonClick={props.onEnterAmountsClick}
                                    />
                                ) : null}
                            </div>
                        </>
                    ) : null}
                    {!row.adjustmentEntries?.length && !row.isComplexAdjustment ? (
                        <EditableNumberDiv
                            role='gridcell'
                            className={mergeClasses(
                                styles.reportAmountColumn,
                                styles.alignContentCenter,
                                styles.fontWeightBold,
                                styles.fontSize,
                                shouldShowEnterAmounts ? styles.disabled : undefined,
                                props.rowChangeSummary?.changed ? styles.changedBackground : undefined
                            )}
                            value={row.amount!}
                            onBlur={props.onBlur}
                            data-automation-key='amount'
                        />
                    ) : (
                        <div
                            role='gridcell'
                            className={mergeClasses(
                                styles.reportAmountColumn,
                                styles.alignContentCenter,
                                styles.fontWeightBold,
                                styles.fontSize,
                                shouldShowEnterAmounts ? styles.disabled : undefined,
                                props.rowChangeSummary?.changed ? styles.changedBackground : undefined
                            )}
                            data-automation-key='amount'
                        >
                            {reformatNumericText(row.amount!, formatNumberForDisplay)}
                        </div>
                    )}
                </TreeItemLayout>
            </TreeItem>

            {row.adjustmentEntries
                ? row.adjustmentEntries!.map((adjustmentEntry: AccountAmountWhitePaperRow, index: number) => {
                      const treeLineStyle: string = index === 0 ? treeLineStyles.firstChild : treeLineStyles.subsequentChild;
                      const isLastEntryInThisAdjustment: boolean = index === row.adjustmentEntries!.length - 1;
                      const isLastRowInAccountGroup: boolean = isLastEntryInThisAdjustment && isLastAdjustmentSummary;
                      const entryClassName: string = isLastRowInAccountGroup ? styles.treeRowLast : styles.treeRow;
                      const entryChange = adjustmentEntry.uiKey
                          ? props.updatedChangeTrackingDataMap?.get(adjustmentEntry.uiKey)
                          : undefined;
                      return (
                          <AccountAmountAdjustmentEntryRow
                              row={adjustmentEntry}
                              containerClassName={treeLineStyle}
                              className={entryClassName}
                              key={adjustmentEntry.uiKey}
                              onBlur={props.onBlur}
                              rowChangeSummary={entryChange}
                              onEnterAmountsClick={props.onEnterAmountsClick}
                              onJournalEntryClick={props.onJournalEntryClick}
                              journalEntryButtonDisplayState={props.journalEntryButtonDisplayState}
                          />
                      );
                  })
                : null}
        </>
    );
};
